import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CreatePaymentParams, log } from '@ecommerce/shared'
import { getSessionItem, removeSessionItem, setSessionItem } from '../../utils/browser'

export type YapeStep = 'user-data' | 'verification' | 'confirmation'

interface YapePaymentState {
  step: YapeStep
  authorizationNumber: string | null
  userData: CreatePaymentParams | null
  timestamp: number
  error: string | null
}

const getInitialState = (): YapePaymentState => {
  const initialState: YapePaymentState = {
    step: 'user-data',
    authorizationNumber: null,
    userData: null,
    timestamp: Date.now(),
    error: null,
  }

  if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
    try {
      const storedData = getSessionItem('yapePaymentSession')
      if (storedData) {
        const parsedData = JSON.parse(storedData)
        if (Date.now() - parsedData.timestamp < 10 * 60 * 1000) {
          return parsedData
        }
      }
    } catch (e) {
      log.error('🔥 Error loading Yape payment state🔥', e)
    }
  }

  return initialState
}

const paymentSlice = createSlice({
  name: 'payment',
  initialState: getInitialState(),
  reducers: {
    setStep: (state: YapePaymentState, action: PayloadAction<YapeStep>) => {
      state.step = action.payload
      state.timestamp = Date.now()
      setSessionItem('yapePaymentSession', JSON.stringify(state))
    },
    setAuthorizationNumber: (state, action: PayloadAction<string>) => {
      state.authorizationNumber = action.payload
      state.timestamp = Date.now()
      setSessionItem('yapePaymentSession', JSON.stringify(state))
    },
    setUserData: (state, action: PayloadAction<YapePaymentState['userData']>) => {
      state.userData = action.payload
      setSessionItem('yapePaymentSession', JSON.stringify(state))
    },
    resetPayment(state) {
      state.step = 'user-data'
      state.userData = null
      state.authorizationNumber = null
      state.timestamp = Date.now()
      state.error = null
      removeSessionItem('yapePaymentSession')
    },
  },
})

export const { setStep, setAuthorizationNumber, setUserData, resetPayment } = paymentSlice.actions

export default paymentSlice.reducer
